import React, { useState, useEffect } from 'react';
import { 
  Brain, 
  Target, 
  TrendingUp, 
  TrendingDown,
  Scale,
  Handshake,
  AlertTriangle,
  CheckCircle,
  ChevronRight,
  Play,
  RefreshCw,
  Info,
  DollarSign,
  BarChart3,
  Layers,
  Zap,
  Shield,
  ArrowRight,
  ArrowLeftRight
} from 'lucide-react';
import { api } from '../../utils/api';
import { toast } from 'sonner';
import { FinancialValue } from '../../components/shared/FinancialValue';

interface GameTheoryResult {
  analysis_id: string;
  optimal_settlement: number;
  settlement_range: {
    floor: number;
    ceiling: number;
    nash_equilibrium: number;
    recommended_target: number;
  };
  zopa: {
    exists: boolean;
    range: number;
    floor: number;
    ceiling: number;
    midpoint: number;
    analysis: string;
  };
  nash_equilibrium: {
    point: number;
    claimant_surplus: number;
    insurer_surplus: number;
    fairness_index: number;
    stability: string;
    explanation: string;
  };
  positions: {
    claimant: {
      opening: number;
      target: number;
      reservation: number;
      batna: number;
      confidence: number;
    };
    insurer: {
      opening: number;
      target: number;
      reservation: number;
      batna: number;
      confidence: number;
    };
  };
  batna_analysis: {
    claimant: {
      value: number;
      strength: string;
      recommendation: string;
    };
    insurer: {
      value: number;
      implication: string;
    };
    relative_power: {
      advantage: string;
      claimant_leverage_score: number;
      negotiation_implication: string;
    };
  };
  concession_strategy: {
    recommended_style: string;
    opening_offer: number;
    target_rounds: number;
    moves: Array<{
      round: number;
      offer: number;
      concession: number;
      concession_pct: number;
      rationale: string;
      expected_counter: number;
      p_accepted: number;
    }>;
    total_concession: number;
    expected_outcome: number;
  };
  recommendations: Array<{
    priority: number;
    category: string;
    title: string;
    recommendation: string;
    rationale: string;
  }>;
  inputs: {
    claim_value: number;
    adjuster_value: number;
    attorney_value: number;
    negotiation_style: string;
  };
}

interface NegotiationStyle {
  id: string;
  name: string;
  description: string;
  best_for: string;
  characteristics: {
    opening_multiplier: number;
    concession_speed: string;
    typical_rounds: number;
  };
}

interface GameTheoryDashboardProps {
  initialClaimValue?: number;
}

export default function GameTheoryDashboard({ initialClaimValue }: GameTheoryDashboardProps = {}) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<GameTheoryResult | null>(null);
  const [styles, setStyles] = useState<NegotiationStyle[]>([]);
  const [activeTab, setActiveTab] = useState<'input' | 'results' | 'strategy'>('input');

  // Form state
  const [claimValue, setClaimValue] = useState<string>(initialClaimValue ? String(initialClaimValue) : '500000');
  const [adjusterValue, setAdjusterValue] = useState<string>('');
  const [attorneyValue, setAttorneyValue] = useState<string>('');
  const [selectedStyle, setSelectedStyle] = useState<string>('competitive');
  const [useAutoEstimate, setUseAutoEstimate] = useState(true);

  useEffect(() => {
    fetchStyles();
  }, []);

  const fetchStyles = async () => {
    try {
      const data = await api.get('/api/v1/game-theory/styles');
      setStyles(data.styles || []);
    } catch (error) {
      console.error('Error fetching styles:', error);
      // Set default styles if API fails
      setStyles([
        { id: 'aggressive', name: 'Aggressive', description: 'Push for maximum recovery', best_for: 'Strong BATNA cases', characteristics: { opening_multiplier: 1.15, concession_speed: 'slow', typical_rounds: 6 } },
        { id: 'competitive', name: 'Competitive', description: 'Strategic, calculated moves', best_for: 'Standard negotiations', characteristics: { opening_multiplier: 1.05, concession_speed: 'moderate', typical_rounds: 5 } },
        { id: 'cooperative', name: 'Cooperative', description: 'Seek mutual agreement', best_for: 'Ongoing relationships', characteristics: { opening_multiplier: 0.95, concession_speed: 'fast', typical_rounds: 4 } },
        { id: 'accommodating', name: 'Accommodating', description: 'Quick settlement', best_for: 'Time-sensitive cases', characteristics: { opening_multiplier: 0.90, concession_speed: 'very_fast', typical_rounds: 3 } }
      ]);
    }
  };

  const runAnalysis = async () => {
    const claim = parseFloat(claimValue);
    if (isNaN(claim) || claim <= 0) {
      toast.error('Please enter a valid claim value');
      return;
    }

    setLoading(true);
    try {
      let data;
      if (useAutoEstimate) {
        data = await api.post('/api/v1/game-theory/quick-analyze', {
          claim_value: claim,
          negotiation_style: selectedStyle
        });
      } else {
        const adjuster = parseFloat(adjusterValue) || claim * 0.75;
        const attorney = parseFloat(attorneyValue) || claim * 1.25;
        data = await api.post('/api/v1/game-theory/analyze', {
          claim_value: claim,
          adjuster_value: adjuster,
          attorney_value: attorney,
          negotiation_style: selectedStyle
        });
      }
      setResult(data);
      setActiveTab('results');
      toast.success('Analysis complete!');
    } catch (error) {
      console.error('Analysis error:', error);
      toast.error('Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const getStabilityStyle = (stability: string): React.CSSProperties => {
    switch (stability) {
      case 'highly_stable': return { color: 'var(--color-verified)', backgroundColor: 'transparent' };
      case 'stable': return { color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-needs-human-tint)' };
      case 'moderately_stable': return { color: 'var(--color-brand)', backgroundColor: 'var(--color-needs-human-tint)' };
      default: return { color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-needs-human-tint)' };
    }
  };

  const getAdvantageStyle = (advantage: string): React.CSSProperties => {
    switch (advantage) {
      case 'claimant': return { color: 'var(--color-verified)' };
      case 'balanced': return { color: 'var(--color-needs-human-text)' };
      case 'insurer': return { color: 'var(--color-brand)' };
      default: return { color: 'var(--color-text-secondary)' };
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-md flex items-center justify-center border border-[var(--color-border)]" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
            <Brain className="w-6 h-6" style={{ color: 'var(--color-brand)' }} />
          </div>
          <div>
            <h1 className="cs-h2" style={{ color: 'var(--color-text-primary)' }}>Game Theory Analysis</h1>
            <p style={{ color: 'var(--color-text-muted)' }}>Strategic negotiation intelligence for optimal settlements</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b" style={{ borderColor: 'var(--color-border)' }}>
        {[
          { id: 'input', label: 'Analysis Input', icon: Target },
          { id: 'results', label: 'Results', icon: BarChart3, disabled: !result },
          { id: 'strategy', label: 'Concession Strategy', icon: TrendingUp, disabled: !result },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => !tab.disabled && setActiveTab(tab.id as any)}
            disabled={tab.disabled}
            className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors border-b-2 -mb-px ${
              tab.disabled ? 'cursor-not-allowed' : ''
            }`}
            style={
              activeTab === tab.id
                ? { color: 'var(--color-needs-human-text)', borderColor: 'var(--color-brand)' }
                : tab.disabled
                ? { color: 'var(--color-text-ghost)', borderColor: 'transparent' }
                : { color: 'var(--color-text-muted)', borderColor: 'transparent' }
            }
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Input Tab */}
      {activeTab === 'input' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Input Form */}
          <div className="lg:col-span-2 rounded-md p-6 border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
            <h3 className="cs-h3 mb-4 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
              <DollarSign className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
              Claim Values
            </h3>

            <div className="space-y-4">
              {/* Claim Value */}
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                  Base Claim Value *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-muted)' }}>$</span>
                  <input
                    type="number"
                    value={claimValue}
                    onChange={(e) => setClaimValue(e.target.value)}
                    className="w-full pl-8 pr-4 py-2.5 border rounded-md focus:ring-2"
                    style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }}
                    placeholder="500000"
                  />
                </div>
              </div>

              {/* Auto-estimate toggle */}
              <div className="flex items-center gap-3 p-3 rounded-md" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                <input
                  type="checkbox"
                  id="autoEstimate"
                  checked={useAutoEstimate}
                  onChange={(e) => setUseAutoEstimate(e.target.checked)}
                  className="w-4 h-4 rounded"
                  style={{ accentColor: 'var(--color-brand)' }}
                />
                <label htmlFor="autoEstimate" className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  Auto-estimate adjuster & attorney values (75% / 125% of claim)
                </label>
              </div>

              {/* Manual Values */}
              {!useAutoEstimate && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                      Adjuster Value
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-muted)' }}>$</span>
                      <input
                        type="number"
                        value={adjusterValue}
                        onChange={(e) => setAdjusterValue(e.target.value)}
                        className="w-full pl-8 pr-4 py-2.5 border rounded-md focus:ring-2"
                        style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }}
                        placeholder={String(parseFloat(claimValue) * 0.75 || '')}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                      Attorney Value
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-muted)' }}>$</span>
                      <input
                        type="number"
                        value={attorneyValue}
                        onChange={(e) => setAttorneyValue(e.target.value)}
                        className="w-full pl-8 pr-4 py-2.5 border rounded-md focus:ring-2"
                        style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }}
                        placeholder={String(parseFloat(claimValue) * 1.25 || '')}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Negotiation Style */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                  Negotiation Style
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {styles.map(style => (
                    <button
                      key={style.id}
                      onClick={() => setSelectedStyle(style.id)}
                      className="p-3 rounded-md border-2 text-left transition-all"
                      style={
                        selectedStyle === style.id
                          ? { borderColor: 'var(--color-brand)', backgroundColor: 'var(--color-needs-human-tint)' }
                          : { borderColor: 'var(--color-border)' }
                      }
                    >
                      <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{style.name}</p>
                      <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>{style.characteristics.typical_rounds} rounds</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Run Button */}
              <button
                onClick={runAnalysis}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-md hover:opacity-90 disabled:opacity-50 transition-all font-medium"
                style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
              >
                {loading ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <Play className="w-5 h-5" />
                )}
                {loading ? 'Analyzing...' : 'Run Game Theory Analysis'}
              </button>
            </div>
          </div>

          {/* Style Info */}
          <div className="rounded-md p-6 border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
            <h3 className="cs-h3 mb-4 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
              <Info className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
              Style Guide
            </h3>
            {styles.find(s => s.id === selectedStyle) && (
              <div className="space-y-4">
                <div>
                  <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                    {styles.find(s => s.id === selectedStyle)?.name}
                  </p>
                  <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                    {styles.find(s => s.id === selectedStyle)?.description}
                  </p>
                </div>
                <div className="p-3 rounded-md" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                  <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>Best For:</p>
                  <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                    {styles.find(s => s.id === selectedStyle)?.best_for}
                  </p>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--color-text-muted)' }}>Opening Multiplier</span>
                    <span className="font-medium font-mono tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
                      {((styles.find(s => s.id === selectedStyle)?.characteristics.opening_multiplier || 1) * 100).toFixed(0)}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--color-text-muted)' }}>Concession Speed</span>
                    <span className="font-medium capitalize" style={{ color: 'var(--color-text-primary)' }}>
                      {styles.find(s => s.id === selectedStyle)?.characteristics.concession_speed}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--color-text-muted)' }}>Typical Rounds</span>
                    <span className="font-medium font-mono tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
                      {styles.find(s => s.id === selectedStyle)?.characteristics.typical_rounds}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Results Tab */}
      {activeTab === 'results' && result && (
        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="rounded-md p-5 border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
                <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Optimal Settlement</span>
              </div>
              <p className="text-2xl font-bold font-mono tabular-nums" style={{ color: 'var(--color-text-primary)' }}><FinancialValue value={result.optimal_settlement} /></p>
            </div>
            <div className="rounded-md p-5 border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
              <div className="flex items-center gap-2 mb-2">
                <Scale className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
                <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Nash Equilibrium</span>
              </div>
              <p className="text-2xl font-bold font-mono tabular-nums" style={{ color: 'var(--color-needs-human-text)' }}><FinancialValue value={result.nash_equilibrium.point} /></p>
            </div>
            <div className="rounded-md p-5 border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
              <div className="flex items-center gap-2 mb-2">
                <Layers className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>ZOPA Range</span>
              </div>
              <p className="text-2xl font-bold font-mono tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
                {result.zopa.exists ? <FinancialValue value={result.zopa.range} /> : 'No ZOPA'}
              </p>
            </div>
            <div className="rounded-md p-5 border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
              <div className="flex items-center gap-2 mb-2">
                <Handshake className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
                <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Expected Outcome</span>
              </div>
              <p className="text-2xl font-bold font-mono tabular-nums" style={{ color: 'var(--color-needs-human-text)' }}><FinancialValue value={result.concession_strategy.expected_outcome} /></p>
            </div>
          </div>

          {/* ZOPA Visualization */}
          <div className="rounded-md p-6 border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
            <h3 className="cs-h3 mb-4 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
              <ArrowLeftRight className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
              Zone of Possible Agreement (ZOPA)
            </h3>

            {result.zopa.exists ? (
              <div className="space-y-4">
                <div className="relative h-16 rounded-md overflow-hidden" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                  {/* ZOPA Zone */}
                  <div
                    className="absolute h-full"
                    style={{
                      backgroundColor: 'var(--color-border-strong)',
                      left: `${((result.zopa.floor - result.inputs.adjuster_value * 0.7) / (result.inputs.attorney_value * 1.15 - result.inputs.adjuster_value * 0.7)) * 100}%`,
                      width: `${(result.zopa.range / (result.inputs.attorney_value * 1.15 - result.inputs.adjuster_value * 0.7)) * 100}%`
                    }}
                  />
                  {/* Nash Point Marker */}
                  <div
                    className="absolute top-0 h-full w-1"
                    style={{
                      backgroundColor: 'var(--color-brand)',
                      left: `${((result.nash_equilibrium.point - result.inputs.adjuster_value * 0.7) / (result.inputs.attorney_value * 1.15 - result.inputs.adjuster_value * 0.7)) * 100}%`
                    }}
                  >
                    <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-medium whitespace-nowrap font-mono tabular-nums" style={{ color: 'var(--color-needs-human-text)' }}>
                      Nash: <FinancialValue value={result.nash_equilibrium.point} />
                    </div>
                  </div>
                </div>
                <div className="flex justify-between text-sm">
                  <div className="text-left">
                    <p style={{ color: 'var(--color-text-muted)' }}>ZOPA Floor</p>
                    <p className="font-semibold font-mono tabular-nums" style={{ color: 'var(--color-text-primary)' }}><FinancialValue value={result.zopa.floor} /></p>
                  </div>
                  <div className="text-center">
                    <p style={{ color: 'var(--color-text-muted)' }}>Midpoint</p>
                    <p className="font-semibold font-mono tabular-nums" style={{ color: 'var(--color-verified)' }}><FinancialValue value={result.zopa.midpoint} /></p>
                  </div>
                  <div className="text-right">
                    <p style={{ color: 'var(--color-text-muted)' }}>ZOPA Ceiling</p>
                    <p className="font-semibold font-mono tabular-nums" style={{ color: 'var(--color-text-primary)' }}><FinancialValue value={result.zopa.ceiling} /></p>
                  </div>
                </div>
                <p className="text-sm p-3 rounded-md" style={{ color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-bg-elevated)' }}>{result.zopa.analysis}</p>
              </div>
            ) : (
              <div className="flex items-center gap-3 p-4 rounded-md" style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}>
                <AlertTriangle className="w-5 h-5" />
                <p>{result.zopa.analysis}</p>
              </div>
            )}
          </div>

          {/* Nash Equilibrium & BATNA */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Nash Equilibrium */}
            <div className="rounded-md p-6 border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
              <h3 className="cs-h3 mb-4 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                <Scale className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
                Nash Equilibrium
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 rounded-md" style={{ backgroundColor: 'transparent' }}>
                    <p className="text-sm" style={{ color: 'var(--color-verified)' }}>Claimant Surplus</p>
                    <p className="text-xl font-bold font-mono tabular-nums" style={{ color: 'var(--color-verified)' }}>
                      <FinancialValue value={result.nash_equilibrium.claimant_surplus} />
                    </p>
                  </div>
                  <div className="p-3 rounded-md" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                    <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Insurer Surplus</p>
                    <p className="text-xl font-bold font-mono tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
                      <FinancialValue value={result.nash_equilibrium.insurer_surplus} />
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 rounded-md" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                  <span style={{ color: 'var(--color-text-secondary)' }}>Stability</span>
                  <span className="px-3 py-1 rounded-full text-sm font-medium" style={getStabilityStyle(result.nash_equilibrium.stability)}>
                    {result.nash_equilibrium.stability.replace('_', ' ')}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-md" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                  <span style={{ color: 'var(--color-text-secondary)' }}>Fairness Index</span>
                  <span className="font-medium font-mono tabular-nums" style={{ color: 'var(--color-text-primary)' }}>{(result.nash_equilibrium.fairness_index * 100).toFixed(0)}%</span>
                </div>
                <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{result.nash_equilibrium.explanation}</p>
              </div>
            </div>

            {/* BATNA Analysis */}
            <div className="rounded-md p-6 border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
              <h3 className="cs-h3 mb-4 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                <Shield className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
                BATNA Analysis
              </h3>
              <div className="space-y-4">
                <div className="p-3 rounded-md" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                  <div className="flex justify-between items-center">
                    <span style={{ color: 'var(--color-text-secondary)' }}>Your BATNA Value</span>
                    <span className="font-bold font-mono tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
                      <FinancialValue value={result.batna_analysis.claimant.value} />
                    </span>
                  </div>
                  <p className="text-sm mt-2" style={{ color: 'var(--color-text-secondary)' }}>
                    Strength: <span className="font-medium capitalize">{result.batna_analysis.claimant.strength}</span>
                  </p>
                </div>
                <div className="p-3 rounded-md" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                  <div className="flex justify-between items-center">
                    <span style={{ color: 'var(--color-text-secondary)' }}>Power Advantage</span>
                    <span className="font-bold capitalize" style={getAdvantageStyle(result.batna_analysis.relative_power.advantage)}>
                      {result.batna_analysis.relative_power.advantage}
                    </span>
                  </div>
                  <div className="mt-2 h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                    <div
                      className="h-full"
                      style={{ backgroundColor: 'var(--color-brand)', width: `${result.batna_analysis.relative_power.claimant_leverage_score * 100}%` }}
                    />
                  </div>
                  <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                    Leverage Score: <span className="font-mono tabular-nums">{(result.batna_analysis.relative_power.claimant_leverage_score * 100).toFixed(0)}%</span>
                  </p>
                </div>
                <p className="text-sm p-3 rounded-md" style={{ color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-bg-elevated)' }}>
                  {result.batna_analysis.relative_power.negotiation_implication}
                </p>
              </div>
            </div>
          </div>

          {/* Recommendations */}
          <div className="rounded-md p-6 border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
            <h3 className="cs-h3 mb-4 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
              <Zap className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
              Strategic Recommendations
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {result.recommendations.map((rec, idx) => (
                <div key={idx} className="p-4 border rounded-md transition-colors" style={{ borderColor: 'var(--color-border)' }}>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
                      <span className="font-bold text-sm font-mono tabular-nums" style={{ color: 'var(--color-needs-human-text)' }}>{rec.priority}</span>
                    </div>
                    <div>
                      <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{rec.title}</p>
                      <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>{rec.recommendation}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Strategy Tab */}
      {activeTab === 'strategy' && result && (
        <div className="space-y-6">
          {/* Strategy Overview */}
          <div className="rounded-md p-6 border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
            <h3 className="cs-h3 mb-4 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
              <TrendingUp className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
              Concession Strategy ({result.concession_strategy.recommended_style})
            </h3>

            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="text-center p-3 rounded-md" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Opening Offer</p>
                <p className="text-lg font-bold font-mono tabular-nums" style={{ color: 'var(--color-needs-human-text)' }}>
                  <FinancialValue value={result.concession_strategy.opening_offer} />
                </p>
              </div>
              <div className="text-center p-3 rounded-md" style={{ backgroundColor: 'transparent' }}>
                <p className="text-sm" style={{ color: 'var(--color-verified)' }}>Target Rounds</p>
                <p className="text-lg font-bold font-mono tabular-nums" style={{ color: 'var(--color-verified)' }}>
                  {result.concession_strategy.target_rounds}
                </p>
              </div>
              <div className="text-center p-3 rounded-md" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Total Concession</p>
                <p className="text-lg font-bold font-mono tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
                  <FinancialValue value={result.concession_strategy.total_concession} />
                </p>
              </div>
              <div className="text-center p-3 rounded-md" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Expected Outcome</p>
                <p className="text-lg font-bold font-mono tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
                  <FinancialValue value={result.concession_strategy.expected_outcome} />
                </p>
              </div>
            </div>

            {/* Concession Timeline */}
            <div className="space-y-4">
              <h4 className="font-medium" style={{ color: 'var(--color-text-primary)' }}>Recommended Offer Sequence</h4>
              <div className="relative">
                {result.concession_strategy.moves.map((move, idx) => (
                  <div key={idx} className="flex items-start gap-4 mb-4 last:mb-0">
                    {/* Timeline Line */}
                    <div className="relative">
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center font-mono tabular-nums"
                        style={
                          idx === 0
                            ? { backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }
                            : { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }
                        }
                      >
                        {move.round}
                      </div>
                      {idx < result.concession_strategy.moves.length - 1 && (
                        <div className="absolute top-10 left-1/2 -translate-x-1/2 w-0.5 h-16" style={{ backgroundColor: 'var(--color-border)' }} />
                      )}
                    </div>

                    {/* Move Details */}
                    <div className="flex-1 rounded-md p-4" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                          Your Offer: <span className="font-mono tabular-nums"><FinancialValue value={move.offer} /></span>
                        </span>
                        <span className="text-sm font-mono tabular-nums" style={{ color: 'var(--color-brand)' }}>
                          -{move.concession_pct}% concession
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                        <ArrowRight className="w-4 h-4" />
                        <span>Expected Counter: <span className="font-mono tabular-nums"><FinancialValue value={move.expected_counter} /></span></span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span style={{ color: 'var(--color-text-muted)' }}>{move.rationale}</span>
                        <span
                          className="px-2 py-1 rounded-full text-xs font-medium font-mono tabular-nums"
                          style={
                            move.p_accepted > 0.7 ? { backgroundColor: 'transparent', color: 'var(--color-verified)' } :
                            move.p_accepted > 0.4 ? { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-brand)' } :
                            { backgroundColor: 'var(--color-bg-hover)', color: 'var(--color-text-secondary)' }
                          }
                        >
                          {(move.p_accepted * 100).toFixed(0)}% likely accepted
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Position Comparison */}
          <div className="rounded-md p-6 border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
            <h3 className="cs-h3 mb-4" style={{ color: 'var(--color-text-primary)' }}>Position Comparison</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b" style={{ borderColor: 'var(--color-border)' }}>
                    <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>Position</th>
                    <th className="text-right py-3 px-4 text-sm font-medium" style={{ color: 'var(--color-verified)' }}>Claimant</th>
                    <th className="text-right py-3 px-4 text-sm font-medium" style={{ color: 'var(--color-needs-human-text)' }}>Insurer</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b" style={{ borderColor: 'var(--color-border-faint)' }}>
                    <td className="py-3 px-4" style={{ color: 'var(--color-text-secondary)' }}>Opening Position</td>
                    <td className="py-3 px-4 text-right font-medium font-mono tabular-nums" style={{ color: 'var(--color-verified)' }}>
                      <FinancialValue value={result.positions.claimant.opening} />
                    </td>
                    <td className="py-3 px-4 text-right font-medium font-mono tabular-nums" style={{ color: 'var(--color-needs-human-text)' }}>
                      <FinancialValue value={result.positions.insurer.opening} />
                    </td>
                  </tr>
                  <tr className="border-b" style={{ borderColor: 'var(--color-border-faint)' }}>
                    <td className="py-3 px-4" style={{ color: 'var(--color-text-secondary)' }}>Target Position</td>
                    <td className="py-3 px-4 text-right font-medium font-mono tabular-nums" style={{ color: 'var(--color-verified)' }}>
                      <FinancialValue value={result.positions.claimant.target} />
                    </td>
                    <td className="py-3 px-4 text-right font-medium font-mono tabular-nums" style={{ color: 'var(--color-needs-human-text)' }}>
                      <FinancialValue value={result.positions.insurer.target} />
                    </td>
                  </tr>
                  <tr className="border-b" style={{ borderColor: 'var(--color-border-faint)' }}>
                    <td className="py-3 px-4" style={{ color: 'var(--color-text-secondary)' }}>Reservation Price</td>
                    <td className="py-3 px-4 text-right font-medium font-mono tabular-nums" style={{ color: 'var(--color-verified)' }}>
                      <FinancialValue value={result.positions.claimant.reservation} />
                    </td>
                    <td className="py-3 px-4 text-right font-medium font-mono tabular-nums" style={{ color: 'var(--color-needs-human-text)' }}>
                      <FinancialValue value={result.positions.insurer.reservation} />
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4" style={{ color: 'var(--color-text-secondary)' }}>BATNA Value</td>
                    <td className="py-3 px-4 text-right font-medium font-mono tabular-nums" style={{ color: 'var(--color-verified)' }}>
                      <FinancialValue value={result.positions.claimant.batna} />
                    </td>
                    <td className="py-3 px-4 text-right font-medium font-mono tabular-nums" style={{ color: 'var(--color-needs-human-text)' }}>
                      <FinancialValue value={result.positions.insurer.batna} />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
